import type { RefObject } from 'react';
import type { BoxWithItems, MarkerType } from '@shared/types';

/** A corner point in 2D space */
export interface Point2D {
  x: number;
  y: number;
}

/** A single detected marker (ArUco or QR) */
export interface DetectedMarker {
  markerType: MarkerType; // 'aruco' | 'qr'
  markerId: string; // numeric string for aruco ("5"), data string for qr
  corners: [Point2D, Point2D, Point2D, Point2D]; // clockwise from top-left
}

/** Unique key for a detected marker */
export function markerKey(marker: { markerType: MarkerType; markerId: string }): string {
  return `${marker.markerType}:${marker.markerId}`;
}

/** Detection result from a single frame */
export interface DetectionResult {
  markers: DetectedMarker[];
  timestamp: number; // performance.now()
  frameWidth: number; // detection resolution width
  frameHeight: number; // detection resolution height
}

/** Camera status */
export type CameraStatus = 'idle' | 'requesting' | 'active' | 'error';

/** Camera error detail */
export interface CameraError {
  name: string; // e.g. 'NotAllowedError', 'NotFoundError'
  message: string;
}

/** Options for useCamera */
export interface UseCameraOptions {
  facingMode?: 'environment' | 'user';
  width?: number; // ideal width (default 1280)
  height?: number; // ideal height (default 720)
  autoStart?: boolean; // default false
}

/** Return type of useCamera */
export interface UseCameraReturn {
  videoRef: RefObject<HTMLVideoElement | null>;
  status: CameraStatus;
  error: CameraError | null;
  start: () => Promise<void>;
  stop: () => void;
}

/** Options for useDetection */
export interface UseDetectionOptions {
  enabled?: boolean; // default true
  frameSkip?: number; // process every Nth frame (default 3)
  maxDetectionWidth?: number; // downscale to this width (default 640)
  enableAruco?: boolean; // default true
  enableQr?: boolean; // default true
  enableBarcode?: boolean; // default false
}

/** Return type of useDetection */
export interface UseDetectionReturn {
  detections: DetectionResult | null;
  isDetecting: boolean;
  fps: number; // actual detection FPS
}

/** A tracked marker with persistence and resolved box data */
export interface TrackedMarker extends DetectedMarker {
  lastSeen: number; // performance.now()
  firstSeen: number; // performance.now()
  box?: BoxWithItems;
}

/** Return type of useMarkerTracking */
export interface UseMarkerTrackingReturn {
  trackedMarkers: TrackedMarker[];
  cameraStatus: CameraStatus;
  cameraError: CameraError | null;
  startCamera: () => Promise<void>;
  stopCamera: () => void;
  isDetecting: boolean;
  fps: number;
}
